import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    province: '',
    enquiry_type: 'General Enquiry',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const provinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Limpopo',
    'Mpumalanga',
    'North West',
    'Northern Cape'
  ];

  const enquiryTypes = [
    'General Enquiry',
    'Residential Installation',
    'Commercial Installation',
    'Fleet Solutions',
    'Portable Chargers',
    'Maintenance & Support',
    'Partnership Opportunity'
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        province: '',
        enquiry_type: 'General Enquiry',
        message: ''
      });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Failed to submit. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section
        data-testid="contact-hero-section"
        className="relative py-32 bg-gradient-to-br from-[#0A3D2E] to-[#07291F] text-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter mb-6 font-['Outfit']">
              Get in Touch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Ready to power your electric future? We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="contact-content-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-3xl font-semibold text-[#0A3D2E] mb-8 font-['Outfit']">
                    Contact Information
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#E9F0EC] rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Mail size={24} className="text-[#0A3D2E]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#0A3D2E] mb-1">Email</h3>
                        <a
                          href="mailto:info@greencircuitev.co.za"
                          className="text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
                        >
                          info@greencircuitev.co.za
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#E9F0EC] rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Phone size={24} className="text-[#0A3D2E]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#0A3D2E] mb-1">Phone</h3>
                        <a
                          href="tel:0108228366"
                          className="text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
                        >
                          010 822 8366
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#E9F0EC] rounded-2xl flex items-center justify-center flex-shrink-0">
                        <MapPin size={24} className="text-[#0A3D2E]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-[#0A3D2E] mb-1">Location</h3>
                        <p className="text-[#4A5D54]">
                          30 Paperbark Rd, Fourways<br />Sandton, 2055<br />Johannesburg, South Africa
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0A3D2E] text-white p-8 rounded-2xl">
                  <h3 className="text-xl font-semibold mb-3 font-['Outfit']">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span>8:00 AM - 5:00 PM</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Saturday:</span>
                      <span>9:00 AM - 1:00 PM</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Sunday:</span>
                      <span>Closed</span>
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="bg-white p-8 lg:p-12 rounded-2xl border border-[#E2E8F0]">
                  <h2 className="text-3xl font-semibold text-[#0A3D2E] mb-6 font-['Outfit']">
                    Send Us a Message
                  </h2>
                  
                  {submitted && (
                    <div data-testid="success-message" className="mb-6 p-4 bg-[#CDFF00] text-[#0A3D2E] rounded-2xl">
                      Thank you! We'll get back to you within 24 hours.
                    </div>
                  )}

                  {error && (
                    <div data-testid="error-message" className="mb-6 p-4 bg-red-100 text-red-800 rounded-2xl">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          data-testid="contact-name-input"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          data-testid="contact-email-input"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          data-testid="contact-phone-input"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          data-testid="contact-company-input"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Province
                        </label>
                        <select
                          name="province"
                          data-testid="contact-province-select"
                          value={formData.province}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        >
                          <option value="">Select Province</option>
                          {provinces.map((prov) => (
                            <option key={prov} value={prov}>{prov}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                          Enquiry Type *
                        </label>
                        <select
                          name="enquiry_type"
                          data-testid="contact-enquiry-type-select"
                          value={formData.enquiry_type}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                        >
                          {enquiryTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#0A3D2E] mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        data-testid="contact-message-textarea"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={submitting}
                      className="w-full px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      <Send size={20} />
                      <span>{submitting ? 'Sending...' : 'Send Message'}</span>
                    </button>
                  </form>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
